/**
 * Work orders — `mockups/studio/client-detail.html` §Work orders.
 *
 * The mockup's own comment: "projects. The August extras live in the ledger above, so
 * they are not listed twice." That is DESIGN.md P2 as a filter — this table is
 * `kind === 'project'` and nothing else, and the head note counts the extras rather
 * than printing them a second time.
 *
 * **Nudge is a decision's button, not a row's.** A proposed project is one of
 * `detail.decisions`, and that is where the reminder mail's amount and dates come
 * from (`NudgeButton`); a project with no open decision has no Nudge, because there
 * is nothing to remind anybody about.
 *
 * **The reference is the link.** Session 24 gave a work order a route of its own
 * (`/studio/work-orders/$ref`), which closes the gap the header used to describe: the
 * ledger states what a project is and the page it opens is where the studio acts on
 * it. The link is on the `Ref` cell rather than around the row, because a row that is
 * one big anchor cannot hold the Nudge button in its last cell.
 *
 * **An extra still has no row here, and still has a page.** This table is
 * `kind === 'project'` by design (the head note counts the extras rather than printing
 * them a second time), so `/studio/work-orders/REQ-118` is reached from the task board
 * and from a project's own "raised against it" list — which is half of why the screen
 * is a route rather than a row that opens in place.
 *
 * **The Total column is the project *to date*, and now says so** (session 24, M-4).
 * `readmodels/studio-client-detail.ts` `withScopeChangesFolded` adds every billable
 * scope change into a project's `total_amount`, which is what `client-detail.html`
 * prints — `WO-2026-008` at $5,050.00, the four milestones' $4,730.00 plus REQ-117's
 * $320.00. The mockup carries "1 scope change attached" underneath it and this table
 * did not, so the studio's own two screens read $5,050.00 here and "Agreed total
 * $4,730.00" on the work order's page with nothing naming the difference — while the
 * client's Projects page reconciles the identical pair in a sentence.
 *
 * `scope_changes` is the read model's three numbers and this prints them. It states no
 * figure the payload does not carry: the sub-line under a folded total is the quote and
 * the delta as they arrived, and the fold below the table is the sentence that says
 * which of the two the work order's own page prints. A project with nothing folded gets
 * neither, because then there is nothing to reconcile.
 *
 * **An extra has no row in the table, and now has a way in.** The head note said
 * "3 projects, 36 extras" and listed only the three: no tab, no filter, no
 * disclosure and no link, so each of the thirty-six was reachable only by typing a
 * reference somebody already knew. Counting a thing and then hiding it is the shape
 * `.docs/24` is about — "a screen that states a fact must offer the decision that
 * fact implies".
 *
 * They are behind a `Fold` rather than in the table, which keeps the mockup's own
 * rule intact ("the August extras live in the ledger above, so they are not listed
 * twice"): the ledger prints the **month's** extras with their money, and this prints
 * the account's whole list as references and states, which is a different question
 * and the only one that answers "where is REQ-118". Closed by default — DESIGN.md P3,
 * the routine folded — and its summary is the count, so the number in the head note
 * and the thing you press are the same number.
 */

import type { StudioClientDetailView, StudioWorkOrderRowView } from '@fareast/shared'

import { Link } from '@tanstack/react-router'

import { ButtonRow } from '../../../components/portal/Button.js'
import { Card, CardBody } from '../../../components/portal/Card.js'
import { Empty, Fold } from '../../../components/portal/Disclosure.js'
import { ScrollX, Section, SectionHead } from '../../../components/portal/Layout.js'
import { Pill } from '../../../components/portal/Status.js'
import { TBody, THead, Table, Td, Th, Tr } from '../../../components/portal/Table.js'
import {
  B,
  DateText,
  Linkish,
  Muted,
  Num,
  Prose,
  Ref,
  Small,
  Xs,
} from '../../../components/portal/Text.js'
import { STATUS_WORD } from '../../../components/studio/workOrderWords.js'
import { useFormat } from '../../../format/useFormat.js'
import { NudgeButton } from './NudgeButton.js'

export const WORK_ORDERS_ID = 'work-orders'

export type WorkOrdersSectionProps = {
  readonly detail: StudioClientDetailView
}

export function WorkOrdersSection({ detail }: WorkOrdersSectionProps) {
  const format = useFormat()
  const projects = detail.work_orders.filter((one) => one.kind === 'project')
  const extras = detail.work_orders.filter((one) => one.kind === 'extra')
  /* Whether anything in this table is a folded figure at all — the fold below only
     belongs on a page where one of these rows reads higher than its own quote. */
  const folded = projects.some((one) => (one.scope_changes ?? null) !== null)

  return (
    <Section id={WORK_ORDERS_ID}>
      <SectionHead
        title="Work orders"
        note={`${format.qty(projects.length, { one: 'project', other: 'projects' })}, ${format.qty(
          extras.length,
          { one: 'extra', other: 'extras' },
        )}`}
      />

      {projects.length === 0 ? (
        <Empty>No projects. Every piece of work here is an extra against the retainer.</Empty>
      ) : (
        <Card>
          <CardBody tight scrollX>
            <ScrollX>
              <Table>
                <THead>
                  <Tr>
                    <Th scope="col">Ref</Th>
                    <Th scope="col">Project</Th>
                    <Th scope="col" right>
                      Total
                    </Th>
                    <Th scope="col">State</Th>
                    <Th scope="col">Approved</Th>
                    <Th scope="col" right>
                      <span>Waiting on them</span>
                    </Th>
                  </Tr>
                </THead>
                <TBody>
                  {projects.map((project) => (
                    <ProjectRow
                      key={project.ref}
                      client={detail.client}
                      project={project}
                      decision={detail.decisions.find((one) => one.ref === project.ref) ?? null}
                    />
                  ))}
                </TBody>
              </Table>
            </ScrollX>
          </CardBody>
        </Card>
      )}

      {folded ? (
        <Fold summary="Why a project here can read higher than its own page">
          <Prose>
            A project&rsquo;s total in this table is the <B>project to date</B>: its agreed quote
            plus every scope change approved against it since. Its own page prints the{' '}
            <B>agreed total</B> — the fixed figure the milestone percentages are worked out on — and
            a scope change is listed there separately, under &ldquo;Raised against it&rdquo;. Both
            are right, and the row above says which is which.
          </Prose>
          <Prose>
            A scope change bills on the project&rsquo;s final milestone, so the two figures come
            back together on the last invoice. One that was declined, cancelled or waived is in
            neither.
          </Prose>
        </Fold>
      ) : null}
      {extras.length === 0 ? null : (
        <Fold
          summary={`${format.qty(extras.length, { one: 'extra', other: 'extras' })} against the retainer`}
        >
          <Card>
            <CardBody tight scrollX>
              <ScrollX>
                <Table>
                  <THead>
                    <Tr>
                      <Th scope="col">Ref</Th>
                      <Th scope="col">Extra</Th>
                      <Th scope="col" right>
                        Total
                      </Th>
                      <Th scope="col">State</Th>
                      <Th scope="col">Raised</Th>
                    </Tr>
                  </THead>
                  <TBody>
                    {extras.map((extra) => (
                      <ExtraRow key={extra.ref} extra={extra} />
                    ))}
                  </TBody>
                </Table>
              </ScrollX>
            </CardBody>
          </Card>
        </Fold>
      )}
    </Section>
  )
}

/**
 * One extra: its reference, what it was, what it came to and where it got to.
 *
 * No Nudge, no "waiting on them" column and **no ochre**. An extra the client has not
 * answered is already a row in the decisions card at the top of this page, and
 * DESIGN.md P4 gives ochre one meaning per screen — a second, quieter copy of the same
 * claim inside a closed disclosure would be the page saying it twice and meaning it
 * once. `NudgeButton` belongs to a decision rather than to a work order, which is the
 * same rule the projects table follows.
 */
function ExtraRow({ extra }: { readonly extra: StudioWorkOrderRowView }) {
  return (
    <Tr>
      <Td ref>
        <Linkish asChild>
          <Link to="/studio/work-orders/$ref" params={{ ref: extra.ref }}>
            <Ref value={extra.ref} nowrap />
          </Link>
        </Linkish>
      </Td>
      <Td>
        <B>{extra.title}</B>
      </Td>
      <Td right num>
        {extra.total_amount === null ? (
          <Muted>not priced</Muted>
        ) : (
          <Num value={extra.total_amount} />
        )}
      </Td>
      <Td>
        <Pill>{STATUS_WORD[extra.status]}</Pill>
      </Td>
      <Td>
        {extra.proposed_at === null ? (
          <Small>
            <Muted>—</Muted>
          </Small>
        ) : (
          <Small>
            <Muted>
              <DateText value={extra.proposed_at} />
            </Muted>
          </Small>
        )}
      </Td>
    </Tr>
  )
}

function ProjectRow({
  client,
  project,
  decision,
}: {
  readonly client: string
  readonly project: StudioWorkOrderRowView
  readonly decision: StudioClientDetailView['decisions'][number] | null
}) {
  const format = useFormat()
  const waiting = project.status === 'proposed'
  /* `?? null` and not `!== null`: the committed recording predates the field, so the
     wire carries `undefined` until `pnpm world:record` is run — see the schema. */
  const folded = project.scope_changes ?? null

  return (
    <Tr variant={waiting ? 'need' : undefined}>
      <Td ref>
        <Linkish asChild>
          <Link to="/studio/work-orders/$ref" params={{ ref: project.ref }}>
            <Ref value={project.ref} nowrap />
          </Link>
        </Linkish>
      </Td>
      <Td>
        <B>{project.title}</B>
        <Xs>
          <Muted>{project.scope}</Muted>
        </Xs>
      </Td>
      <Td right num>
        {/* A draft has no price yet: `total_amount` is null until `work_orders_price`. */}
        {project.total_amount === null ? (
          <Muted>not priced</Muted>
        ) : (
          <Num value={project.total_amount} />
        )}
        {/*
          The reconciliation, on the row that needs it. Both figures are fields —
          `scope_changes.agreed_total` is what this project was quoted at and
          `scope_changes.total` is what has been approved against it since — so the
          reader can get from the number above to the one the work order's own page
          prints without doing any arithmetic, and so can the page.
        */}
        {folded === null ? null : (
          <Xs block>
            <Muted>
              {`${format.money(folded.agreed_total)} agreed + ${format.money(folded.total)} of scope approved since, in `}
              {format.qty(folded.count, { one: 'change', other: 'changes' })}
            </Muted>
          </Xs>
        )}
      </Td>
      <Td>
        <Pill state={waiting ? 'need' : undefined}>{STATUS_WORD[project.status]}</Pill>
      </Td>
      <Td>
        {project.approved_at === null || project.approved_by_name === null ? (
          <Small>
            <Muted>—</Muted>
            {project.proposed_at === null ? null : (
              <Xs>
                <Muted>
                  sent <DateText value={project.proposed_at} />
                </Muted>
              </Xs>
            )}
          </Small>
        ) : (
          <Small>
            {project.approved_by_name}
            <Xs>
              <Muted>
                <DateText value={project.approved_at} />
              </Muted>
            </Xs>
          </Small>
        )}
      </Td>
      <Td right>
        {decision === null ? null : (
          <ButtonRow>
            <NudgeButton client={client} decision={decision} />
          </ButtonRow>
        )}
      </Td>
    </Tr>
  )
}
